// src/config.ts

import { WhiteLabelConfig } from "./contexts/WhiteLabelContext";

// Direct approach using import.meta (works in browser context)
// Reads the build-time environment variable VITE_BACKEND_DOMAIN
const viteBackendDomain = typeof import.meta !== 'undefined' ? import.meta.env.VITE_BACKEND_DOMAIN : undefined;

// Node.js environment approach (fallback, typically not used in frontend build)
const nodeBackendDomain = typeof process !== 'undefined' ? process.env.VITE_BACKEND_DOMAIN : undefined;

// Determine the backend domain, preferring Vite's env vars
const backendDomain = viteBackendDomain ?? nodeBackendDomain;

// For debugging in the browser console
//console.log("Build-time VITE_BACKEND_DOMAIN:", viteBackendDomain);
//console.log("Determined backendDomain:", backendDomain);

let determinedApiBaseUrl: string;

if (backendDomain && backendDomain.trim() !== '') {
  // If VITE_BACKEND_DOMAIN was explicitly set during build (e.g., to a full URL), use it and add /api
  // Ensure no double slashes if backendDomain already ends with /
  const base = backendDomain.endsWith('/') ? backendDomain.slice(0, -1) : backendDomain;
  determinedApiBaseUrl = `${base}/api`;
  //console.log(`API_BASE_URL determined from VITE_BACKEND_DOMAIN: ${determinedApiBaseUrl}`);
} else {
  // --- SIMPLIFIED FALLBACK ---
  // If VITE_BACKEND_DOMAIN was not set or was empty during build, ALWAYS use a relative path.
  // This works for both development (with Vite proxy) and production (served via Caddy/reverse proxy).
  determinedApiBaseUrl = "/api";
  //console.log(`API_BASE_URL determined (relative): ${determinedApiBaseUrl}`);
  // --- END SIMPLIFIED FALLBACK ---
}

// Final assignment, ensure no double /api if accidentally included in backendDomain
// Also remove potential trailing slashes for consistency before adding specific endpoints
export const API_BASE_URL = determinedApiBaseUrl.replace('/api', '/api').replace(/\/$/, '');

//console.log("Final API_BASE_URL:", API_BASE_URL);


// Construct the full health check URL
export const HEALTH_CHECK_URL = `${API_BASE_URL}/health`;
//console.log("Health Check URL:", HEALTH_CHECK_URL);


// Determine DRIVEMONITOR_IS_PROD from environment variables
// First check Vite's import.meta.env for VITE_DRIVEMONITOR_IS_PROD (preferred for browser context)
const viteDrivemonitorIsProd = typeof import.meta !== 'undefined' ? import.meta.env.VITE_DRIVEMONITOR_IS_PROD : undefined;

// Then check Node.js process.env as fallback
const nodeDrivemonitorIsProd = typeof process !== 'undefined' ? process.env.DRIVEMONITOR_IS_PROD : undefined;

// Use the first available value, defaulting to 'false' if neither is defined
const drivemonitorIsProd = viteDrivemonitorIsProd !== undefined 
  ? viteDrivemonitorIsProd 
  : (nodeDrivemonitorIsProd || 'false');

// Convert to boolean for use in the application
const isProd = drivemonitorIsProd === 'true';
export const IS_PROD = isProd;
// Allow explicitly starting a grounding demo on an unreviewed event. Existing
// reviews are shown based on their data, independently of this creation flag.
export const WORKBENCH_GROUNDING_TOOLS_ENABLED = import.meta.env.VITE_WORKBENCH_GROUNDING_TOOLS_ENABLED === 'true';
// process.env.* values are replaced by Vite's define block at build time.
export const EVERYWHERE_SEARCH_ENABLED = process.env.EVERYWHERE_SEARCH_ENABLED === 'true';
export const EVERYWHERE_SEARCH_SAMPLE_ENABLED = process.env.EVERYWHERE_SEARCH_SAMPLE_ENABLED === 'true';
// Parsed from backend/app/utils/constants.py at build time — single source of truth.
export const EVERYWHERE_SEARCH_ADMIN_UIDS: string[] =
  (process.env.EVERYWHERE_SEARCH_ADMIN_UIDS || '').split(',').map((s: string) => s.trim()).filter(Boolean);
// Non-admin accounts allowed to create Dataset Studio datasets from folders
// they can already reach, and to open the datasets they own. Parsed from
// MARKETPLACE_DATASET_CREATOR_UIDS in backend/app/utils/constants.py at build
// time (see vite.config.ts). This grants no admin capability: the catalog,
// admin panel and cross-owner actions stay admin-gated, and the backend
// independently enforces folder access and dataset ownership.
export const MARKETPLACE_DATASET_CREATOR_UIDS: string[] =
  (process.env.MARKETPLACE_DATASET_CREATOR_UIDS || '').split(',').map((s: string) => s.trim()).filter(Boolean);
// "Fast folders" — folder ids whose in-app search is routed through the
// everywhere-search job pipeline (streaming partial results) instead of the
// synchronous /search POST. Parsed from EVERYWHERE_SEARCH_PINNED_FOLDERS in
// backend/app/utils/constants.py at build time (see vite.config.ts), which
// defaults to DrivingDojo if the constant is absent.
export const PINNED_FOLDER_IDS: string[] =
  (process.env.EVERYWHERE_SEARCH_PINNED_FOLDERS || '').split(',').map((s: string) => s.trim()).filter(Boolean);
// Public sample-content owner UIDs. Sample videos and their analyses are owned
// by these accounts and are readable by EVERYONE (logged-in, non-admin, and
// logged-out demo users) per firestore.rules — which whitelists exactly these
// two UIDs for public sample reads. The sample-scope event queries must always
// include these UIDs so sample events render regardless of the build-time
// VITE_ADMIN_UID. Keep in sync with firestore.rules.
export const PUBLIC_SAMPLE_OWNER_UIDS: string[] = [
  "TIRVcKcvM8WYkAdsPeUo1tBb1x43", // samplevideosowner@nomadicml.com
  "gyJh7xtevSb9fEJCdTAEzDrx0xg1", // aadit@nomadicml.com (legacy sample owner)
];

export const YOLO_SEGMENTATION_EXPORT_EMAIL_DOMAINS: string[] = [
  "havelsan.com.tr",
];

export const FIRESTORE_COLLECTION_NAME = isProd ? "videos" : "videos_dev";
export const FIRESTORE_COLLECTION_BATCH_ANALYSES = isProd ? "batch_analyses" : "batch_analyses_dev";
export const FIRESTORE_COLLECTION_EDGE_CASE = isProd ? "edge_case" : "edge_case_dev";
export const FIRESTORE_COLLECTION_EDGE_CASE_CATEGORIES = isProd ? "edge_case_categories" : "edge_case_categories_dev";
export const FIRESTORE_COLLECTION_CONCEPTS = isProd ? "concepts" : "concepts_dev";
export const FIRESTORE_COLLECTION_STATISTICS = isProd ? "statistics" : "statistics_dev";
export const FIRESTORE_COLLECTION_VIDEOFOLDERS = isProd ? "videoFolders" : "videoFolders_dev";
export const FIRESTORE_COLLECTION_LIVESESSIONS = isProd ? "liveSessions" : "liveSessions_dev";
export const FIRESTORE_COLLECTION_STREAMS = isProd ? "streams" : "streams_dev";
export const FIRESTORE_COLLECTION_EVALS = isProd ? "evals" : "evals_dev";
/** Dashboard deep-analysis pipeline (Chain of Thought) snapshots — per-user via `created_by`. */
export const FIRESTORE_COLLECTION_DASHBOARD_REASONING_TRACES = isProd
  ? "dashboard_reasoning_traces"
  : "dashboard_reasoning_traces_dev";
//console.log(`DRIVEMONITOR_IS_PROD: ${isProd}, Firestore Collection: ${FIRESTORE_COLLECTION_NAME}`);


// Application configuration constants
export const AppConfig = {
  healthCheckInterval: 30000, // 30 seconds
};

export const p3OrgConfig: {
  orgId: string,
  config: WhiteLabelConfig
} = {
  orgId: "org_015d0afed148",
  config: {
    isEnabled: false,
    logoUrl: '/p3-group/p3-logo.webp',
    companyName: 'P3 Group',
    primaryColor: '#3E48CC',
    secondaryColor: '#27E6D6',
    motto: "Business as Unusual",
    mottoPosition: 'inline'
  }
}
